/**
 * Piano operativo del giorno di picco (INB-39).
 *
 * Le due curve orarie del giorno di massima produzione — FV dal modello fisico
 * e consumo dai profili di carico dei soci — messe una sull'altra ora per ora.
 * Da lì escono le uniche tre cose che servono a un amministratore che deve
 * scrivere ai soci: in che finestra c'è energia che nessuno sta usando, quanta
 * ne resta scoperta la sera, e quanto del fabbisogno il sole copre da solo.
 *
 * Il modulo torna **fatti**, non testo: finestre, kWh e percentuali. La copy la
 * compone la view, come già per il piano storage — così la stessa logica serve
 * web e mobile senza portarsi dietro una lingua.
 */

export interface PeakDayProfileParams {
	/** Giorno civile Europe/Rome, 'YYYY-MM-DD'. */
	date: string;
	/** 24 valori kWh, indice = ora civile. Produzione FV prevista della CER. */
	productionHours: readonly number[];
	/** 24 valori kWh, indice = ora civile. Consumo previsto della CER. */
	consumptionHours: readonly number[];
}

/** Una finestra oraria contigua: `toHour` è esclusiva (10→15 copre 10:00–15:00). */
export interface PeakDayWindow {
	fromHour: number;
	toHour: number;
	kwh: number;
}

export interface PeakDayPlan {
	available: boolean;
	date: string;
	productionKwh: number;
	consumptionKwh: number;
	/** Ora del massimo di ciascuna curva — non coincidono quasi mai. */
	productionPeakHour: number;
	productionPeakKwh: number;
	consumptionPeakHour: number;
	/** Energia prodotta e consumata nella stessa ora, kWh. */
	selfConsumptionKwh: number;
	/** Quota della produzione autoconsumata sul posto. */
	selfConsumptionPct: number;
	/** Quota del fabbisogno del giorno coperta dalla produzione. */
	coveragePct: number;
	/** Surplus totale del giorno (produzione oltre il consumo istantaneo). */
	surplusKwh: number;
	/** Deficit totale del giorno (consumo oltre la produzione istantanea). */
	deficitKwh: number;
	/**
	 * La finestra contigua che concentra più surplus: è lì che vanno spostati i
	 * carichi differibili. `null` se la produzione non supera mai il consumo.
	 */
	surplusWindow: PeakDayWindow | null;
	/**
	 * Il maggiore blocco contiguo di deficit **dopo** la finestra di surplus —
	 * la coda serale, quando il FV è già sceso e il residenziale sale. È la
	 * finestra da cui conviene togliere carico, non quella da riempire.
	 */
	deficitWindow: PeakDayWindow | null;
}

const HOURS = 24;
const round1 = (n: number) => Math.round(n * 10) / 10;
const round2 = (n: number) => Math.round(n * 100) / 100;

/** 24 valori finiti e non negativi, qualunque cosa sia arrivata. */
function normalizeHours(values: readonly number[] | undefined): number[] {
	const out = new Array<number>(HOURS).fill(0);
	if (!values) return out;
	for (let h = 0; h < HOURS; h += 1) {
		const v = values[h];
		if (typeof v === 'number' && Number.isFinite(v) && v > 0) out[h] = v;
	}
	return out;
}

function peakOf(hours: readonly number[]): number {
	let peak = 0;
	for (let h = 1; h < HOURS; h += 1) {
		if (hours[h]! > hours[peak]!) peak = h;
	}
	return peak;
}

/**
 * Il blocco contiguo di ore positive con la somma maggiore, a partire da
 * `fromHour`. I valori sono già ≥ 0, quindi "massima somma contigua" e "corsa
 * fra due zeri più ricca" coincidono: basta una passata.
 */
function bestWindow(values: readonly number[], fromHour = 0): PeakDayWindow | null {
	let bestFrom = -1;
	let bestTo = -1;
	let bestKwh = 0;
	let start = -1;
	let sum = 0;

	const close = (end: number) => {
		if (start >= 0 && sum > bestKwh) {
			bestFrom = start;
			bestTo = end;
			bestKwh = sum;
		}
		start = -1;
		sum = 0;
	};

	for (let h = fromHour; h < HOURS; h += 1) {
		if (values[h]! > 0) {
			if (start < 0) start = h;
			sum += values[h]!;
		} else {
			close(h);
		}
	}
	close(HOURS);

	return bestFrom < 0 ? null : { fromHour: bestFrom, toHour: bestTo, kwh: round1(bestKwh) };
}

/**
 * Confronta le due curve del giorno di picco e ne ricava la finestra in cui
 * spostare i carichi, quella da alleggerire e la copertura del fabbisogno.
 *
 * Surplus e deficit sono calcolati **ora per ora**, non sui totali: un giorno
 * che produce quanto consuma può benissimo esportare a mezzogiorno e comprare
 * alle otto di sera, ed è esattamente la differenza che l'amministratore deve
 * comunicare ai soci.
 */
export function buildPeakDayPlan(params: PeakDayProfileParams): PeakDayPlan {
	const production = normalizeHours(params.productionHours);
	const consumption = normalizeHours(params.consumptionHours);
	const productionKwh = production.reduce((sum, kwh) => sum + kwh, 0);
	const consumptionKwh = consumption.reduce((sum, kwh) => sum + kwh, 0);

	const empty: PeakDayPlan = {
		available: false,
		date: params.date,
		productionKwh: round1(productionKwh),
		consumptionKwh: round1(consumptionKwh),
		productionPeakHour: 0,
		productionPeakKwh: 0,
		consumptionPeakHour: 0,
		selfConsumptionKwh: 0,
		selfConsumptionPct: 0,
		coveragePct: 0,
		surplusKwh: 0,
		deficitKwh: 0,
		surplusWindow: null,
		deficitWindow: null,
	};
	// Senza produzione non c'è un giorno di picco di cui parlare.
	if (productionKwh <= 0) return empty;

	const surplusHours = production.map((p, h) => Math.max(0, p - consumption[h]!));
	const deficitHours = consumption.map((c, h) => Math.max(0, c - production[h]!));
	const selfConsumptionKwh = production.reduce(
		(sum, p, h) => sum + Math.min(p, consumption[h]!),
		0,
	);
	const surplusKwh = surplusHours.reduce((sum, kwh) => sum + kwh, 0);
	const deficitKwh = deficitHours.reduce((sum, kwh) => sum + kwh, 0);

	const productionPeakHour = peakOf(production);
	const surplusWindow = bestWindow(surplusHours);
	// Il deficit che interessa è quello che *segue* il surplus: la coda serale.
	// Cercarlo su tutto il giorno restituirebbe spesso le ore prima dell'alba,
	// dove non c'è nulla da spostare perché il sole non è ancora sorto.
	const deficitWindow = bestWindow(deficitHours, surplusWindow?.toHour ?? 0);

	return {
		available: true,
		date: params.date,
		productionKwh: round1(productionKwh),
		consumptionKwh: round1(consumptionKwh),
		productionPeakHour,
		productionPeakKwh: round2(production[productionPeakHour]!),
		consumptionPeakHour: peakOf(consumption),
		selfConsumptionKwh: round1(selfConsumptionKwh),
		selfConsumptionPct: Math.round((selfConsumptionKwh / productionKwh) * 100),
		coveragePct: consumptionKwh > 0 ? Math.round((selfConsumptionKwh / consumptionKwh) * 100) : 0,
		surplusKwh: round1(surplusKwh),
		deficitKwh: round1(deficitKwh),
		surplusWindow,
		deficitWindow,
	};
}
